import React from 'react';
import { Box, IconButton, useSnackbar } from 'pounceV2';
import { useNotifications } from 'Components/Notifications/graphql/Notifications.generated';
import { getNewNotifications } from 'Components/Notifications/utils';
import NotificationSnackbarV3 from 'Components/Notifications/NotificationSnackbarV3';
import storage from 'Helpers/storage';
import { useIdleTimer } from 'react-idle-timer';
import useSidesheetV3 from 'Hooks/useSidesheetV3';
import { SIDESHEETS } from 'Components/utils/Sidesheet/SidesheetContextV3';
import CSVNotificationSnackbarV3 from 'Components/Notifications/CSVNotificationSnackbarV3';

type variant = 'info' | 'warning' | 'error';

const SEEN_NOTIFICATIONS_KEY = 'seen-notifications-ids';

export const DEFAULT_POLL_INTERVAL = 1 * 60 * 1000; // 1 minute
export const DEFAULT_IDLE_TIMEOUT = 5 * 60 * 1000; // 5 minutes

const NotificationsMenuV3 = () => {
  const { showSidesheet } = useSidesheetV3();
  const { pushSnackbar } = useSnackbar();

  // Keep track of the notifications that have been seen as snackbar
  const [seenNotifications, setSeenNotifications] = React.useState(
    () => storage.session.read<string>(SEEN_NOTIFICATIONS_KEY) ?? [],
  );

  // Request the latest 10 notifications every 1 minute
  const { data, startPolling, stopPolling } = useNotifications({
    fetchPolicy: 'network-only',
    pollInterval: DEFAULT_POLL_INTERVAL,
    variables: { input: { pageSize: 10 } },
    context: { failSilently: true },
  });

  const onActive = React.useCallback(() => {
    startPolling(DEFAULT_POLL_INTERVAL);
  }, [startPolling]);

  const onIdle = React.useCallback(() => {
    stopPolling();
  }, [stopPolling]);

  useIdleTimer({
    timeout: DEFAULT_IDLE_TIMEOUT,
    onActive,
    onIdle,
  });

  const notifications = data?.notifications.edges.map(edge => edge.node) ?? [];

  const hasUnreadNotifications = notifications.some(
    notification => Date.parse(notification.readAt) < 0,
  );

  // Use the getNewNotifications function to get new notifications
  const newNotifications = getNewNotifications(notifications, 60);

  React.useEffect(() => {
    const delayMs = 500;
    const unseenNotifications = newNotifications.filter(
      notification => !seenNotifications.includes(notification.id),
    );

    if (unseenNotifications.length > 0) {
      // Mark all notifications as seen immediately
      const newSeenIds = unseenNotifications.map(notification => notification.id);
      const updatedSeenNotifications = [...seenNotifications, ...newSeenIds];

      storage.session.write(SEEN_NOTIFICATIONS_KEY, JSON.stringify(updatedSeenNotifications));
      setSeenNotifications(updatedSeenNotifications);

      // Display notifications with staggered delays
      unseenNotifications.forEach((notification, i) => {
        const delayAmount = i * delayMs;
        setTimeout(() => {
          if (notification.metadata?.originalQueryId) {
            pushSnackbar({
              as: CSVNotificationSnackbarV3,
              variant: notification.level.toLowerCase() as variant,
              duration: 10000,
              customProps: { notification },
            });
          } else {
            pushSnackbar({
              as: NotificationSnackbarV3,
              title: notification.title,
              description: notification.message,
              variant: notification.level.toLowerCase() as variant,
              position: 'bottom-right',
              duration: 10000,
              discardable: true,
              customProps: { notification },
            });
          }
        }, delayAmount);
      });
    }
  }, [newNotifications, pushSnackbar, seenNotifications]);

  return (
    <>
      <Box display="flex">
        <IconButton
          id="notification-menu"
          icon={hasUnreadNotifications ? 'notifications-unread' : 'notification-outline'}
          variant="ghost"
          data-tid="view-notifications-button"
          aria-label="Toggle Notifications Center"
          onClick={() => showSidesheet({ sidesheet: SIDESHEETS.NOTIFICATION_CENTER })}
        />
      </Box>
    </>
  );
};

export default React.memo(NotificationsMenuV3);
